import { clamp01 } from '../core/math.js';

/** Band boundaries in Hz. Treble is capped well below Nyquist to avoid dead bins. */
export const BASS_RANGE_HZ = { from: 20, to: 160 } as const;
export const MID_RANGE_HZ = { from: 160, to: 2000 } as const;
export const TREBLE_RANGE_HZ = { from: 2000, to: 8000 } as const;

export interface FrequencyBands {
  readonly bass: number;
  readonly mid: number;
  readonly treble: number;
  readonly level: number;
}

/**
 * Map a frequency in Hz to the index of the FFT bin that contains it.
 * `spectrumLength` is the number of bins (analyser frequencyBinCount, i.e. fftSize / 2).
 */
export function frequencyToBin(
  frequencyHz: number,
  sampleRate: number,
  spectrumLength: number,
): number {
  const nyquist = sampleRate / 2;
  const ratio = clamp01(frequencyHz / nyquist);
  return Math.min(spectrumLength - 1, Math.round(ratio * (spectrumLength - 1)));
}

/** Average byte-magnitude of the bins in [fromBin, toBin], normalised to [0, 1]. */
export function bandAverage(
  spectrum: Readonly<Uint8Array>,
  fromBin: number,
  toBin: number,
): number {
  const first = Math.max(0, fromBin);
  const last = Math.min(spectrum.length - 1, toBin);
  if (last < first) {
    return 0;
  }
  let sum = 0;
  for (let i = first; i <= last; i += 1) {
    sum += spectrum[i] ?? 0;
  }
  const count = last - first + 1;
  return clamp01(sum / (count * 255));
}

/** Split a byte frequency spectrum into normalised bass / mid / treble / overall level. */
export function extractBands(spectrum: Readonly<Uint8Array>, sampleRate: number): FrequencyBands {
  const bins = spectrum.length;
  if (bins === 0) {
    return { bass: 0, mid: 0, treble: 0, level: 0 };
  }
  const bass = bandAverage(
    spectrum,
    frequencyToBin(BASS_RANGE_HZ.from, sampleRate, bins),
    frequencyToBin(BASS_RANGE_HZ.to, sampleRate, bins),
  );
  const mid = bandAverage(
    spectrum,
    frequencyToBin(MID_RANGE_HZ.from, sampleRate, bins),
    frequencyToBin(MID_RANGE_HZ.to, sampleRate, bins),
  );
  const treble = bandAverage(
    spectrum,
    frequencyToBin(TREBLE_RANGE_HZ.from, sampleRate, bins),
    frequencyToBin(TREBLE_RANGE_HZ.to, sampleRate, bins),
  );
  const level = bandAverage(spectrum, 0, bins - 1);
  return { bass, mid, treble, level };
}
